import { dataPortalLocale } from '../i18n';
import { defineStore } from 'pinia';
import { onScopeDispose, ref } from 'vue';
import { dataPortalAuthApi } from '../api/data-portal-auth.api';
import {
  dataPortalApiBaseUrl,
  dataPortalHttpStatus,
  dataPortalRefreshConflictDeadlineMs,
  dataPortalRefreshConflictMaxDelayMs,
} from '../api/data-portal.client';
import type {
  DataPortalRefreshResult,
  DataPortalUser,
} from './data-portal-auth.types';

const logoutChannelName = 'ghm-data-portal-auth';
const logoutStorageKey = 'ghm:data-portal:logout';
export const dataPortalLogoutIncompleteStorageKey =
  'ghm:data-portal:logout-incomplete';

type LogoutIncompleteReason = 'partial' | 'failed';

export const useDataPortalAuthStore = defineStore('dataPortalAuth', () => {
  let hasLogoutMarker = false;
  let storedLogoutIncomplete: LogoutIncompleteReason | null = null;
  try {
    hasLogoutMarker = window.localStorage.getItem(logoutStorageKey) !== null;
    const storedReason = window.localStorage.getItem(
      dataPortalLogoutIncompleteStorageKey,
    );
    if (storedReason === 'partial' || storedReason === 'failed') {
      storedLogoutIncomplete = storedReason;
    }
  } catch {
    hasLogoutMarker = false;
  }
  const user = ref<DataPortalUser | null>(null);
  const logoutIncomplete = ref<LogoutIncompleteReason | null>(
    storedLogoutIncomplete,
  );
  const status = ref<
    | 'unknown'
    | 'loading'
    | 'authenticated'
    | 'anonymous'
    | 'forbidden'
    | 'unavailable'
  >(hasLogoutMarker ? 'anonymous' : 'unknown');
  let loadPromise: Promise<boolean> | null = null;
  let logoutPromise: Promise<void> | null = null;
  let logoutTerminal = hasLogoutMarker;
  let generation = 0;

  let logoutChannel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      logoutChannel = new BroadcastChannel(logoutChannelName);
    } catch {
      logoutChannel = null;
    }
  }

  function terminateLocalSession(): void {
    generation += 1;
    loadPromise = null;
    logoutTerminal = true;
    user.value = null;
    status.value = 'anonymous';
  }

  function broadcastLogout(): void {
    try {
      window.localStorage.setItem(
        logoutStorageKey,
        `${Date.now()}:${Math.random()}`,
      );
    } catch {
      // The in-memory terminal state still prevents local reauthentication.
    }
    try {
      logoutChannel?.postMessage('logout');
    } catch {
      // The persistent marker remains the cross-tab fallback.
    }
  }

  const onLogoutMessage = (event: MessageEvent<unknown>): void => {
    if (event.data === 'logout') terminateLocalSession();
  };
  const onLogoutStorage = (event: StorageEvent): void => {
    if (event.key === logoutStorageKey) {
      if (event.newValue) {
        terminateLocalSession();
      } else {
        logoutTerminal = false;
      }
    }
    if (event.key === dataPortalLogoutIncompleteStorageKey) {
      logoutIncomplete.value =
        event.newValue === 'partial' || event.newValue === 'failed'
          ? event.newValue
          : null;
    }
  };

  if (logoutChannel) {
    logoutChannel.addEventListener('message', onLogoutMessage);
  }
  window.addEventListener('storage', onLogoutStorage);

  onScopeDispose(() => {
    if (logoutChannel) {
      logoutChannel.removeEventListener('message', onLogoutMessage);
      logoutChannel.close();
    }
    window.removeEventListener('storage', onLogoutStorage);
  });

  function setAuthenticated(
    requestGeneration: number,
    authenticatedUser: DataPortalUser,
  ): boolean {
    if (requestGeneration !== generation) {
      return status.value === 'authenticated' && user.value !== null;
    }
    user.value = authenticatedUser;
    status.value = 'authenticated';
    return true;
  }

  function setFailure(requestGeneration: number, error: unknown): boolean {
    if (requestGeneration !== generation) {
      return status.value === 'authenticated' && user.value !== null;
    }
    user.value = null;
    const responseStatus = dataPortalHttpStatus(error);
    status.value =
      responseStatus === 401
        ? 'anonymous'
        : responseStatus === 403
          ? 'forbidden'
          : 'unavailable';
    return false;
  }

  const wait = (milliseconds: number) =>
    new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

  async function loadUserGeneration(
    requestGeneration: number,
  ): Promise<boolean> {
    if (!user.value) status.value = 'loading';
    try {
      return setAuthenticated(requestGeneration, await dataPortalAuthApi.me());
    } catch (error: unknown) {
      if (dataPortalHttpStatus(error) !== 401)
        return setFailure(requestGeneration, error);
    }

    let refreshResult: DataPortalRefreshResult;
    try {
      refreshResult = await dataPortalAuthApi.refresh();
    } catch (error: unknown) {
      return setFailure(requestGeneration, error);
    }

    if (refreshResult.status === 'refreshed') {
      try {
        return setAuthenticated(
          requestGeneration,
          await dataPortalAuthApi.me(),
        );
      } catch (error: unknown) {
        return setFailure(requestGeneration, error);
      }
    }

    const conflictDeadline = Date.now() + dataPortalRefreshConflictDeadlineMs;
    let conflictDelay = Math.max(250, refreshResult.retryAfterMs);
    while (Date.now() < conflictDeadline) {
      const remaining = conflictDeadline - Date.now();
      await wait(Math.min(conflictDelay, remaining));
      if (requestGeneration !== generation)
        return status.value === 'authenticated' && user.value !== null;
      try {
        return setAuthenticated(
          requestGeneration,
          await dataPortalAuthApi.me(),
        );
      } catch (error: unknown) {
        if (dataPortalHttpStatus(error) !== 401)
          return setFailure(requestGeneration, error);
      }
      conflictDelay = Math.min(
        dataPortalRefreshConflictMaxDelayMs,
        Math.max(250, conflictDelay * 2),
      );
    }
    if (requestGeneration !== generation)
      return status.value === 'authenticated' && user.value !== null;
    status.value = 'unavailable';
    return false;
  }

  function loadUser(): Promise<boolean> {
    if (logoutPromise) return logoutPromise.then(() => false);
    if (logoutTerminal) return Promise.resolve(false);
    if (loadPromise) return loadPromise;
    const requestGeneration = ++generation;
    const request = loadUserGeneration(requestGeneration).finally(() => {
      if (loadPromise === request) loadPromise = null;
    });
    loadPromise = request;
    return request;
  }

  function login(returnTo = '/data-portal'): void {
    generation += 1;
    loadPromise = null;
    logoutTerminal = false;
    try {
      window.localStorage.removeItem(logoutStorageKey);
      window.localStorage.removeItem(dataPortalLogoutIncompleteStorageKey);
      logoutIncomplete.value = null;
    } catch {
      // Explicit login still clears the in-memory terminal state.
    }
    const safeReturnTo =
      returnTo === '/data-portal' ||
      returnTo.startsWith('/data-portal/') ||
      returnTo.startsWith('/data-portal?')
        ? returnTo
        : '/data-portal';
    const loginUrl = new URL(
      `${dataPortalApiBaseUrl.replace(/\/$/, '')}/auth/login`,
      window.location.origin,
    );
    loginUrl.searchParams.set('returnTo', safeReturnTo);
    loginUrl.searchParams.set('locale', dataPortalLocale());
    window.location.assign(loginUrl.toString());
  }

  function logout(): Promise<void> {
    if (logoutPromise) return logoutPromise;

    terminateLocalSession();
    broadcastLogout();

    const request = dataPortalAuthApi
      .logout()
      .then((result) => {
        const serverLogoutComplete =
          result.status === 'complete' &&
          result.sessionTombstoned &&
          result.refreshTokenRevoked !== false;
        if (!serverLogoutComplete) markLogoutIncomplete('partial');
        if (result.logoutUrl) {
          window.location.assign(result.logoutUrl);
          return;
        }
        if (!serverLogoutComplete) showLogoutIncomplete();
      })
      .catch(() => {
        markLogoutIncomplete('failed');
        showLogoutIncomplete();
      })
      .finally(() => {
        if (logoutPromise === request) logoutPromise = null;
      });
    logoutPromise = request;
    return request;
  }

  function markLogoutIncomplete(reason: LogoutIncompleteReason): void {
    logoutIncomplete.value = reason;
    try {
      window.localStorage.setItem(dataPortalLogoutIncompleteStorageKey, reason);
    } catch {
      // The in-memory marker remains visible until navigation.
    }
  }

  function showLogoutIncomplete(): void {
    window.location.assign(
      new URL('/data-portal/login', window.location.origin).toString(),
    );
  }

  return { user, status, logoutIncomplete, loadUser, login, logout };
});
